import { extendTheme, useMultiStyleConfig, FormControl, FormLabel, InputGroup, InputLeftElement, chakra, InputRightElement, FormErrorMessage, FormHelperText, Input, Select, keyframes as keyframes$1, Button, IconButton, HStack, Box, useToast, useTheme, Flex, Grid } from '@chakra-ui/react';
import { __assign, __rest, __makeTemplateObject } from 'tslib';
import { memoizedGet, omit } from '@chakra-ui/utils';
import { keyframes } from '@chakra-ui/system';
import { mode } from '@chakra-ui/theme-tools';
import { theme as theme$1 } from '@chakra-ui/theme';
import React, { useState, forwardRef, useRef, useEffect } from 'react';

/**
 * Very basic implementation of conversion of HEX to RGB do not use for precise handling.
 */
function convertHextoRGBColor(hexColor) {
    var match = hexColor.match(/.{1,2}/g);
    if (!match)
        return null;
    var aRgb = [
        parseInt(match[0], 16),
        parseInt(match[1], 16),
        parseInt(match[2], 16)
    ];
    return aRgb;
}
/**
 * Basic Typescript condition assertion for narrowing types.
 *
 * @see https://exploringjs.com/tackling-ts/ch_type-guards-assertion-functions.html#asserting-a-boolean-argument-asserts-cond
 */
function assert(condition, msg) {
    if (!condition) {
        throw new Error(msg);
    }
}
/**
 * A helper to replace the depreciated Chakra getColor function.
 * Just does basic defined check validation, look to adding TinyColor package back for more validation scenarios.
 */
function getColor(theme, color, fallback) {
    var hex = memoizedGet(theme, "colors.".concat(color), color);
    return typeof hex === 'undefined' ? fallback : hex;
}
/**
 * Helper to handle a string which is supposed to be a boolean or undefined, where
 * undefined will be false.
 */
function assignStringBoolean(value) {
    return typeof value === 'boolean' ? value : false;
}
/**
 * Helper to handle a string which is supposed to be a boolean or undefined, where
 * undefined will be false.
 */
function isDefined(value) {
    return value !== null && value !== undefined;
}

var baseControlWrapperTheme = function (props) { return ({
    display: 'flex',
    zIndex: 1,
    w: '100%',
    height: '56px',
    border: 'none',
    boxSizing: 'border-box',
    boxShadow: "inset 0 0 0 1px ".concat(getColor(props.theme, 'neutral.300')),
    fontStyle: 'normal',
    fontWeight: 'normal',
    color: 'neutral.900',
    outline: 'none',
    borderRadius: 'xl',
    _disabled: {
        border: '1px dashed',
        borderColor: 'neutral.300',
        cursor: 'not-allowed',
        boxShadow: 'none !important',
        color: 'neutral.400'
    },
    _hover: { boxShadow: "inset 0 0 0 1px ".concat(getColor(props.theme, 'neutral.600')) },
    _readOnly: { userSelect: 'all' },
    _invalid: { boxShadow: "inset 0 0 0 1px ".concat(getColor(props.theme, 'error.700')) },
    _focus: {
        zIndex: 1,
        boxShadow: "inset 0 0 0 2px ".concat(getColor(props.theme, 'primary.500'))
    }
}); };
var commonTextStyles = {
    marginTop: '3px',
    width: '100%',
    px: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '12px',
    letterSpacing: '0.4px',
    lineHeight: '20px'
};
/**
 * BaseControl theme that can be used for a common look and feel eg,
 * Select, TextInput and third party components like credit card.
 *
 * @see https://github.com/chakra-ui/chakra-ui/blob/092b20f3e93064c1db312062b76b420a7ed1703b/packages/theme/src/components/input.ts
 * @see https://www.figma.com/file/AVVZO09XE8fZ0uuOaJYcQg/B2C-Onboarding---for-Dev?node-id=2705%3A56301
 */
var BaseControlTheme = {
    parts: [
        'Wrapper',
        'FormControl',
        'FormLabel',
        'FormHelperText',
        'FormErrorMessage',
        'InputRightElement',
        'InputLeftElement'
    ],
    baseStyle: function (_a) {
        var isFocused = _a.isFocused, isInvalid = _a.isInvalid, isDisabled = _a.isDisabled, isWarning = _a.isWarning, isSuccess = _a.isSuccess, isUserWarning = _a.isUserWarning, isHovering = _a.isHovering, isLabelOnTop = _a.isLabelOnTop, hasRightAdornment = _a.hasRightAdornment, theme = _a.theme;
        // Manually get colors for theming boxShadow, a current limitation of Chakra api
        var warning500 = getColor(theme, 'warning.500');
        var secondary400 = getColor(theme, 'secondary.400');
        var success500 = getColor(theme, 'success.500');
        var neutral300 = getColor(theme, 'neutral.300');
        var neutral600 = getColor(theme, 'neutral.600');
        var error700 = getColor(theme, 'error.700');
        var primary500 = getColor(theme, 'primary.500');
        var formLabelColor = function () {
            if (isWarning && isLabelOnTop) {
                return 'warning.700';
            }
            if (isUserWarning && isLabelOnTop) {
                return 'secondary.700';
            }
            if (isSuccess && !isFocused && isLabelOnTop) {
                return 'success.700';
            }
            if (isInvalid) {
                return isLabelOnTop ? 'error.700' : 'neutral.600';
            }
            return isFocused ? 'primary.700' : 'neutral.600';
        };
        var helperTextColor = function () {
            if (isWarning) {
                return 'warning.700';
            }
            if (isUserWarning) {
                return 'secondary.700';
            }
            if (isSuccess && !isFocused) {
                return 'success.700';
            }
            return isDisabled ? 'neutral.400' : 'neutral.600';
        };
        var inputBorderColor = function () {
            if (isInvalid) {
                return "inset 0 0 0 1px ".concat(error700);
            }
            if (isWarning && !isFocused) {
                return "inset 0 0 0 1px ".concat(warning500);
            }
            if (isUserWarning && !isFocused) {
                return "inset 0 0 0 1px ".concat(secondary400);
            }
            if (isSuccess && !isFocused) {
                return "inset 0 0 0 1px ".concat(success500);
            }
            if (isHovering && !isFocused) {
                return "inset 0 0 0 1px ".concat(neutral600);
            }
            return "inset 0 0 0 1px ".concat(neutral300);
        };
        var Wrapper = __assign(__assign({}, baseControlWrapperTheme({
            theme: theme
        })), { boxShadow: inputBorderColor(), _focus: {
                zIndex: 1,
                boxShadow: isInvalid ? "inset 0 0 0 1px ".concat(error700) : "inset 0 0 0 2px ".concat(primary500)
            } });
        // When there is an adornment to the right there is whitespace issues,
        // but only when label is not on top.
        var marginRight = isLabelOnTop ? '16px' : hasRightAdornment ? '42px' : '16px';
        return {
            FormControl: {
                position: 'relative',
                w: '100%'
            },
            Wrapper: Wrapper,
            InputLeftElement: {
                h: 14,
                pointerEvents: 'none'
            },
            InputRightElement: {
                h: 14,
                pointerEvents: 'none'
            },
            FormLabel: {
                position: 'absolute',
                zIndex: 2,
                top: isLabelOnTop ? '2px' : '28px',
                left: '11px',
                paddingLeft: '4px',
                paddingRight: '4px',
                marginRight: marginRight,
                transform: 'translate(0, -50%)',
                transition: 'all 0.2s ease-in-out',
                pointerEvents: 'none',
                fontSize: isLabelOnTop ? '12px' : '16px',
                fontStyle: 'normal',
                fontWeight: '400',
                letterSpacing: '0.15px',
                background: 'white !important',
                color: formLabelColor(),
                _disabled: {
                    color: 'neutral.400',
                    opacity: 1
                }
            },
            FormHelperText: __assign(__assign({}, commonTextStyles), { color: helperTextColor() }),
            FormErrorMessage: __assign(__assign({}, commonTextStyles), { color: isDisabled ? 'neutral.400' : 'error.700' })
        };
    }
};

/**
 * Extends base Button theme with 3p Figma design.
 * See RippleButton for the material ui behaviour, this was not possible in css only in this theme.
 *
 * @see https://github.com/chakra-ui/chakra-ui/blob/092b20f3e93064c1db312062b76b420a7ed1703b/packages/theme/src/components/button.ts
 * @see https://www.figma.com/file/AVVZO09XE8fZ0uuOaJYcQg/B2C-Onboarding---for-Dev?node-id=2759%3A24812
 */
var ButtonTheme = {
    defaultProps: {
        variant: 'primary',
        sizes: 'md'
    },
    baseStyle: {
        fontWeight: '500',
        borderRadius: '80px',
        outline: 'none',
        border: 'none',
        cursor: 'pointer',
        overflow: 'hidden',
        _disabled: {
            opacity: '0.4',
            cursor: 'not-allowed',
            boxShadow: 'none'
        }
    },
    sizes: {
        small: {
            h: '30px',
            px: '16px',
            minW: '30px',
            fontSize: '13px',
            lineHeight: '22px'
        },
        medium: {
            h: '40px',
            px: '24px',
            minW: '40px',
            fontSize: '14px',
            lineHeight: '24px'
        },
        large: {
            h: '52px',
            px: '32px',
            minW: '52px',
            fontSize: '18px',
            lineHeight: '26px'
        }
    },
    variants: {
        primary: {
            bg: 'primary.600',
            color: 'secondary.0',
            _hover: {
                bg: 'primary.700',
                _disabled: { bg: 'primary.600' }
            }
        },
        secondary: {
            backgroundColor: 'secondary.0',
            color: 'primary.600',
            borderColor: 'primary.300',
            borderWidth: '1px',
            borderStyle: 'solid',
            _hover: {
                bg: 'primary.100',
                _disabled: { bg: 'secondary.0' }
            }
        },
        tertiary: {
            backgroundColor: 'secondary.0',
            color: 'primary.600',
            _hover: {
                bg: 'primary.100',
                _disabled: { bg: 'secondary.0' }
            }
        }
    }
};

var baseStyleRequiredIndicator = function () {
    return {
        // This is an intentional override from Design team,
        // no space between astrix and label and no red color.
        marginStart: 0,
        color: 'inherit'
    };
};
var baseStyle$2 = function (props) { return ({ requiredIndicator: baseStyleRequiredIndicator() }); };
var FormTheme = { baseStyle: baseStyle$2 };

var variantDefault$1 = function (props) {
    return { field: __assign({}, baseControlWrapperTheme(props)) };
};
var size$1 = {
    default: {
        h: 14,
        px: 4,
        borderRadius: 'xl',
        fontSize: 'md',
        // TODO missing in design system
        letterSpacing: '0.15px',
        lineHeight: 'base'
    }
};
var sizes$1 = { default: { field: size$1.default } };
var InputTheme = {
    defaultProps: {
        size: 'default',
        variant: 'default'
    },
    variants: { default: variantDefault$1 },
    sizes: sizes$1
};

var baseStyle$1 = {
    cursor: 'pointer',
    textDecoration: 'underline',
    outline: 'none',
    color: 'primary.600',
    _hover: { color: 'primary.700' }
};
var LinkTheme = { baseStyle: baseStyle$1 };

/**
 * Unknown color tokens, need to add to base theme?.
 */
var variantDefault = function (props) {
    return {
        field: __assign({}, baseControlWrapperTheme(props)),
        icon: {}
    };
};
var size = {
    default: {
        h: '56px',
        px: '16px',
        borderRadius: 'xl',
        fontSize: 'md',
        // TODO missing in design system
        letterSpacing: '0.15px',
        lineHeight: 'base'
    }
};
var sizes = { default: { field: size.default } };
var SelectTheme = {
    defaultProps: {
        size: 'default',
        variant: 'default'
    },
    variants: { default: variantDefault },
    sizes: sizes
};

var fade = function (startColor, endColor) {
    return keyframes({
        from: {
            borderColor: startColor,
            background: startColor
        },
        to: {
            borderColor: endColor,
            background: endColor
        }
    });
};
var baseStyle = function (props) {
    var defaultStartColor = mode('neutral.100', 'gray.800')(props);
    var defaultEndColor = mode('neutral.300', 'gray.600')(props);
    var _a = props.startColor, startColor = _a === void 0 ? defaultStartColor : _a, _b = props.endColor, endColor = _b === void 0 ? defaultEndColor : _b, speed = props.speed, theme = props.theme;
    var start = getColor(theme, startColor);
    var end = getColor(theme, endColor);
    return {
        opacity: 0.7,
        borderRadius: '12px',
        borderColor: start,
        background: end,
        animation: "".concat(speed, "s linear infinite alternate ").concat(fade(start, end))
    };
};
var SkeletonTheme = {
    baseStyle: baseStyle,
    defaultProps: { fadeDuration: 1.2 }
};

var components = {
    Button: ButtonTheme,
    Form: FormTheme,
    Input: InputTheme,
    Select: SelectTheme,
    Link: LinkTheme,
    Skeleton: SkeletonTheme,
    BaseControl: BaseControlTheme
};

/**
 * Extend the base shared theme with the custom colors and variants in portal.
 *
 * @see https://chakra-ui.com/docs/theming/customize-theme
 */
var config = {
    useSystemColorMode: false,
    initialColorMode: 'light',
    cssVarPrefix: '3p'
};

/**
 * Mathletics color theme color design tokens.
 * Taken from Figma mui poc, see links.
 *
 * Each time a new token is added we need to run the chakra cli to generate types,
 * this should happen automatically for consumers of this module via
 * postinstall hook, as per chakra doc link below scaling-out-your-project.
 *
 * @see official base theme https://github.com/chakra-ui/chakra-ui/blob/684012d868516412e91eb390379ccf7698536345/packages/theme
 * @see https://www.figma.com/file/0KEmygotdgKlMajCrLbx7c/DS---MUI?node-id=5051%3A20799
 * @see https://chakra-ui.com/docs/theming/customize-theme#scaling-out-your-project
 */
var primary = {
    50: '#EEF8FE',
    100: '#D0F1FD',
    200: '#BCEAFD',
    300: '#99DDF6',
    500: '#00ABE8',
    600: '#007ACC',
    700: '#0063A7',
    800: '#004786',
    900: '#00336F'
};
var secondary = {
    0: '#FFFFFF',
    50: '#FFFAEE',
    100: '#FEF3D6',
    200: '#FEE8AD',
    400: '#F9D258',
    500: '#F4BC22',
    600: '#E4AB10',
    700: '#A56A00',
    800: '#854C00',
    900: '#653A00'
};
var neutral = {
    25: '#F8FBFC',
    50: '#F4F8FA',
    100: '#EDF1F4',
    200: '#DBE0E6',
    300: '#CBD3DC',
    400: '#ADB9C7',
    500: '#838F9E',
    600: '#5C6C7F',
    700: '#33475F',
    800: '#1A304B',
    900: '#001937'
};
var success = {
    50: '#F6FEF4',
    100: '#DEF6DE',
    200: '#BCEDBD',
    300: '#9BE49D',
    400: '#58D25B',
    500: '#29B539',
    600: '#1D9B37',
    700: '#148235',
    800: '#0D6830',
    900: '#024D1F'
};
var error = {
    50: '#FEF6F7',
    100: '#FDEEEF',
    200: '#FBDFE1',
    300: '#F6BFC3',
    400: '#F0939B',
    500: '#EB6973',
    600: '#E22837',
    700: '#C51B29',
    800: '#A21622',
    900: '#81000B'
};
var warning = {
    50: '#FFF5F0',
    100: '#FDEEEF',
    200: '#FDCBB5',
    300: '#FDEEEF',
    400: '#FB986B',
    500: '#FA7E46',
    600: '#E4642A',
    700: '#CC461B',
    800: '#BA3409',
    900: '#972500'
};
// Material-ui like circle animation for RippleButton / RippleIconButton
var ripples = {
    // primary.200
    backgroundColor: '#97F7FC'
};
// Include the base Chakra colors, UX has not completed modifying chakra theme.
// this destructuring removes the colors not being used.
// No other api seem for this atm @see https://github.com/chakra-ui/chakra-ui/issues/4711
var _a = theme$1.colors; 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
_a.twitter; _a.facebook; _a.whatsapp; _a.messenger; _a.linkedin; _a.telegram; var baseColors = __rest(_a, ["twitter", "facebook", "whatsapp", "messenger", "linkedin", "telegram"]);
var colors = __assign(__assign({}, baseColors), { 
    // Include the current custom colours as per Figma design
    warning: warning, error: error, success: success, neutral: neutral, secondary: secondary, primary: primary, ripples: ripples });

/**
 * UX Breakpoints defined in Figma, Chakra work with a "mobile first" min-width media query.
 * The values here are one px more in order to understand min-width easier.
 *
 * For example, if the following responsive requirement for a css property:
 * - mobile the background-color is 'green' (the "base" 0px -> 375px)
 * - tablet the background-color is 'blue' (375px -> 768px)
 * - desktop the background-color is 'red' (768px -> any widescreen eg 1600px)
 *
 * You can use the Chakra breakpoint tokens like this:
 *
<Flex
    sx={{
        w: 20,
        h: 20,
        bg: {
            mobile: 'green',
            tablet: 'blue',
            desktop: 'red'
        },
        // Custom queries can still be done like this:
        '@media screen and (max-width: custom thing px)': {
            bg: 'orange'
        }
    }}
 />
 *
 * @see docs https://chakra-ui.com/docs/styled-system/features/responsive-styles
 */
var breakpoints = {
    mobile: '0px',
    tablet: '376px',
    medium: '450px',
    large: '500px',
    desktop: '769px'
};

var styles = {
    global: function () { return ({
        'a': {
            color: 'primary.600',
            cursor: 'pointer',
            textDecoration: 'underline',
            outline: 'none',
            letterSpacing: '0.15px',
            fontWeight: 'medium',
            fontStyle: 'normal',
            fontSize: '16px',
            _hover: { color: 'primary.700' }
        },
        // using https://github.com/WICG/focus-visible breaks <select> in theme
        // :not(.focus-visible)
        ':focus': {
            outline: 'none'
        }
    }); }
};

/**
 * Base theme for mathletics, based on Designs from UX team in Figma.
 *
 * @see Figma https://www.figma.com/file/AVVZO09XE8fZ0uuOaJYcQg/B2C-Onboarding---for-Dev
 */
var theme = extendTheme({
    colors: colors,
    config: config,
    styles: styles,
    breakpoints: breakpoints,
    components: components
});

/* eslint-enable max-len */
function useBaseControlTheme(props, ref) {
    var id = props.id, _a = props.labelText, labelText = _a === void 0 ? null : _a, _b = props.helperText, helperText = _b === void 0 ? null : _b, _c = props.controlProps, controlProps = _c === void 0 ? {
        value: undefined,
        defaultValue: undefined
    } : _c, _d = props.hasValuePredicate, hasValuePredicate = _d === void 0 ? defaultHasVisibleValuePredicate : _d, _e = props.isSuccess, isSuccess = _e === void 0 ? false : _e, _f = props.isWarning, isWarning = _f === void 0 ? false : _f, renderControl = props.renderControl, _g = props.assignProps, assignProps = _g === void 0 ? noop : _g, _h = props.assignControlProps, assignControlProps = _h === void 0 ? noop : _h, _j = props.testFocused, testFocused = _j === void 0 ? false : _j, _k = props.testHover, testHover = _k === void 0 ? false : _k, restProps = __rest(props, ["id", "labelText", "helperText", "controlProps", "hasValuePredicate", "isSuccess", "isWarning", "renderControl", "assignProps", "assignControlProps", "testFocused", "testHover"]);
    var _l = useState(testHover), isHovering = _l[0], setIsHovering = _l[1];
    var _m = useState(testFocused), isFocused = _m[0], setIsFocused = _m[1];
    var _o = useState(hasValuePredicate((controlProps === null || controlProps === void 0 ? void 0 : controlProps.value) || (controlProps === null || controlProps === void 0 ? void 0 : controlProps.defaultValue) || '', props)), hasValue = _o[0], setHasValue = _o[1];
    var isReadOnly = assignStringBoolean(restProps.isReadOnly);
    var state = {
        isUserWarning: assignStringBoolean(restProps.isUserWarning),
        isInvalid: assignStringBoolean(restProps.isInvalid),
        isDisabled: assignStringBoolean(restProps.isDisabled),
        isLabelOnTop: hasValue || isFocused && !isReadOnly,
        isReadOnly: isReadOnly,
        isFocused: isFocused,
        hasValue: hasValue,
        isWarning: isWarning,
        isSuccess: isSuccess,
        isHovering: isHovering,
        hasLeftAdornment: isDefined(props.LeftAdornment),
        hasRightAdornment: isDefined(props.RightAdornment)
    };
    var formControlInternalProps = assignProps(props, state);
    var LeftAdornment = formControlInternalProps.LeftAdornment || null;
    var RightAdornment = formControlInternalProps.RightAdornment || null;
    state.hasLeftAdornment = isDefined(formControlInternalProps.LeftAdornment);
    state.hasRightAdornment = isDefined(formControlInternalProps.RightAdornment);
    var styles = useMultiStyleConfig('BaseControl', state);
    var handleChange = function (event) {
        var _a;
        setHasValue(hasValuePredicate(event.target.value, props));
        ((_a = props.controlProps) === null || _a === void 0 ? void 0 : _a.onChange) && props.controlProps.onChange(event);
    };
    var handleFocus = function (event) {
        setIsFocused(true);
        props.onFocus && props.onFocus(event);
    };
    var handleBlur = function (event) {
        setIsFocused(false);
        props.onBlur && props.onBlur(event);
    };
    var handleOver = function (event) {
        setIsHovering(true);
        props.onMouseOver && props.onMouseOver(event);
    };
    var handleOut = function (event) {
        setIsHovering(false);
        props.onMouseOut && props.onMouseOut(event);
    };
    var _p = styles.Wrapper, 
    // manually apply styled-system props could be done with mergeThemeOverride maybe
    _focus = _p._focus; _p._hover; var _disabled = _p._disabled, baseStyles = __rest(_p, ["_focus", "_hover", "_disabled"])
    // @ts-ignore Chakra types don't support this abstraction :/
    ;
    var wrapperStyles = __assign(__assign(__assign(__assign({}, baseStyles), isFocused ? _focus : null), props.isDisabled ? _disabled : null), props.wrapperStyles);
    var controlRenderProps = assignControlProps(__assign(__assign({}, controlProps), { onFocus: handleFocus, onBlur: handleBlur, onChange: handleChange, children: restProps.children, ref: ref }), formControlInternalProps, state);
    // Manual omit `shouldForwardProp` for properties not to pass to the Chakra
    // FormControl component, otherwise they end up in the DOM :/
    var formControlProps = omit(restProps, ['isUserWarning', 'LeftAdornment', 'RightAdornment']);
    return {
        id: id,
        hasValue: hasValue,
        labelText: labelText,
        helperText: helperText,
        state: state,
        styles: styles,
        wrapperStyles: wrapperStyles,
        controlRenderProps: controlRenderProps,
        formControlProps: formControlProps,
        onOver: handleOver,
        onOut: handleOut,
        renderControl: renderControl,
        LeftAdornment: LeftAdornment,
        RightAdornment: RightAdornment
    };
}
function noop(props, state) {
    // Intentional no operation
    return props;
}
function defaultHasVisibleValuePredicate(value) {
    if (typeof value !== 'string') {
        return false;
    }
    return value.length > 0;
}

function BaseControlInner(originalProps, ref) {
    var _a = useBaseControlTheme(originalProps, ref), formControlProps = _a.formControlProps, id = _a.id, hasValue = _a.hasValue, styles = _a.styles, isInvalid = _a.state.isInvalid, labelText = _a.labelText, onOver = _a.onOver, onOut = _a.onOut, wrapperStyles = _a.wrapperStyles, controlRenderProps = _a.controlRenderProps, renderControl = _a.renderControl, helperText = _a.helperText, LeftAdornment = _a.LeftAdornment, RightAdornment = _a.RightAdornment;
    return (React.createElement(FormControl, __assign({}, formControlProps, { "data-id": id, "data-has-value": hasValue, position: 'relative', sx: styles.FormControl, onMouseOver: onOver, onMouseOut: onOut }),
        labelText ?
            React.createElement(FormLabel, { htmlFor: id, noOfLines: 1, sx: styles.FormLabel, title: labelText }, labelText) : null,
        React.createElement(InputGroup, null,
            LeftAdornment ?
                React.createElement(InputLeftElement, { sx: styles.InputLeftElement, children: LeftAdornment }) : null,
            React.createElement(chakra.div, { "data-testid": 'wrapper', sx: wrapperStyles }, renderControl(controlRenderProps)),
            RightAdornment ?
                React.createElement(InputRightElement, { sx: styles.InputRightElement, children: RightAdornment }) : null),
        helperText ?
            isInvalid ?
                React.createElement(FormErrorMessage, { "data-testid": "helper-text-".concat(id), "data-label-type": 'error', sx: styles.FormErrorMessage }, helperText) :
                React.createElement(FormHelperText, { "data-testid": "helper-text-".concat(id), "data-label-type": 'helper', sx: styles.FormHelperText }, helperText) : null));
}
// This pattern is used to make generics work, there maybe a better way?
// ref: https://fettblog.eu/typescript-react-generic-forward-refs
var BaseControlWithRef = forwardRef(BaseControlInner);
function BaseControl(_a) {
    var mRef = _a.mRef, props = __rest(_a, ["mRef"]);
    return React.createElement(BaseControlWithRef, __assign({ ref: mRef }, props));
}

var SuccessIcon = function (props) { return (React.createElement(chakra.svg, __assign({ width: '24px', height: '24px', viewBox: '0 0 24 24', fill: 'none' }, props),
    React.createElement("path", { fillRule: 'evenodd', clipRule: 'evenodd', d: 'M11.9999 21.6C17.3018 21.6 21.5999 17.302 21.5999 12C21.5999 6.69809 17.3018 2.40002 11.9999 2.40002C6.69797 2.40002 2.3999 6.69809 2.3999 12C2.3999 17.302 6.69797 21.6 11.9999 21.6ZM16.4484 10.4486C16.9171 9.97992 16.9171 9.22012 16.4484 8.7515C15.9798 8.28287 15.22 8.28287 14.7514 8.7515L10.7999 12.703L9.24843 11.1515C8.7798 10.6829 8.02 10.6829 7.55137 11.1515C7.08274 11.6201 7.08274 12.3799 7.55137 12.8486L9.95137 15.2486C10.42 15.7172 11.1798 15.7172 11.6484 15.2486L16.4484 10.4486Z', fill: '#1D9B37' }))); };

var UserIssueIcon = function (props) { return (React.createElement(chakra.svg, __assign({ width: '24px', height: '24px', viewBox: '0 0 24 24', fill: 'none' }, props),
    React.createElement("path", { d: 'M12.0001 10.8C13.9883 10.8 15.6001 9.1882 15.6001 7.19998C15.6001 5.21175 13.9883 3.59998 12.0001 3.59998C10.0119 3.59998 8.4001 5.21175 8.4001 7.19998C8.4001 9.1882 10.0119 10.8 12.0001 10.8Z', fill: '#F4BC22' }),
    React.createElement("path", { d: 'M3.6001 21.6C3.6001 16.9608 7.36091 13.2 12.0001 13.2C16.6393 13.2 20.4001 16.9608 20.4001 21.6H3.6001Z', fill: '#F4BC22' }))); };

var WarningIcon = function (props) { return (React.createElement(chakra.svg, __assign({ width: '24px', height: '24px', viewBox: '0 0 24 24', fill: 'none' }, props),
    React.createElement("path", { fillRule: 'evenodd', clipRule: 'evenodd', d: 'M9.90847 3.71861C10.826 2.08745 13.1745 2.08745 14.092 3.71861L20.7884 15.6233C21.6883 17.2231 20.5322 19.1999 18.6966 19.1999H5.30388C3.4683 19.1999 2.31219 17.2231 3.2121 15.6233L9.90847 3.71861ZM13.2001 15.6C13.2001 16.2628 12.6629 16.8 12.0001 16.8C11.3374 16.8 10.8001 16.2628 10.8001 15.6C10.8001 14.9373 11.3374 14.4 12.0001 14.4C12.6629 14.4 13.2001 14.9373 13.2001 15.6ZM12.0001 6.00002C11.3374 6.00002 10.8001 6.53728 10.8001 7.20002V10.8C10.8001 11.4628 11.3374 12 12.0001 12C12.6629 12 13.2001 11.4628 13.2001 10.8V7.20002C13.2001 6.53728 12.6629 6.00002 12.0001 6.00002Z', fill: '#FF9D00' }))); };

/**
 * A Form Control component to handle user text input inside a form.
 * It is designed from the Material UI design and UX with tweaks from 3p UX team.
 *
 * The React ref is passed through to the input element for usage in scenarios like Chakra Popup.
 *
 * @see the story 'Components/TextInputControl' for states and features.
 */
var TextInputControl = forwardRef(function (_a, ref) {
    var controlProps = _a.controlProps, restProps = __rest(_a, ["controlProps"]);
    return (React.createElement(BaseControl, __assign({}, restProps, { controlProps: controlProps, assignControlProps: function (controlProps, props) {
            return __assign(__assign({}, props.RightAdornment ? { marginRight: '40px' } : null), controlProps);
        }, assignProps: function (props, _a) {
            var isWarning = _a.isWarning, isUserWarning = _a.isUserWarning, isSuccess = _a.isSuccess;
            // Add icons according to the state of the control
            var RightAdornment = restProps.RightAdornment || null;
            if (isWarning) {
                RightAdornment = React.createElement(WarningIcon, null);
            }
            if (isUserWarning) {
                RightAdornment = React.createElement(UserIssueIcon, null);
            }
            if (isSuccess) {
                RightAdornment = React.createElement(SuccessIcon, null);
            }
            return __assign(__assign({}, props), { LeftAdornment: restProps.LeftAdornment || null, RightAdornment: RightAdornment });
        }, renderControl: function (props) { return (React.createElement(Input, __assign({ variant: 'unstyled', autoComplete: 'off', width: '100%', px: '16px' }, props, { ref: ref }))); } })));
});

var SelectIcon = function (props) { return (React.createElement(chakra.svg, __assign({ width: '10px !important', viewBox: '0 0 10 5', fill: 'none' }, props),
    React.createElement("path", { d: 'M0 0L5 5L10 0H0Z', fill: '#5C6C7F' }))); };

var SelectControl = forwardRef(function (restProps, ref) {
    return (React.createElement(BaseControl, __assign({}, restProps, { hasValuePredicate: hasValuePredicate, assignControlProps: function (controlProps, props, state) {
            return __assign(__assign({}, controlProps), { placeholder: props.placeholder });
        }, renderControl: function (_a) {
            var _b = _a.icon, icon = _b === void 0 ? React.createElement(SelectIcon, null) : _b, children = _a.children, 
            // @ts-ignore this prop is moved up and not on the base
            _c = _a.placeholder, 
            // @ts-ignore this prop is moved up and not on the base
            placeholder = _c === void 0 ? null : _c, restControlProps = __rest(_a, ["icon", "children", "placeholder"]);
            return (React.createElement(Select, __assign({ autoComplete: 'off', variant: 'unstyled', width: '100%', height: '56px', icon: icon, placeholder: placeholder, sx: {
                    pl: '16px',
                    pr: '34px'
                } }, restControlProps, { ref: ref }), children));
        } })));
});
function hasValuePredicate(value, props) {
    var isEmptyString = typeof props.labelText === 'string' && props.labelText.length > 0;
    if (isEmptyString && props.placeholder !== ' ') {
        return true;
    }
    if (value === '') {
        return false;
    }
    if (props.children) {
        return true;
    }
    if (typeof value !== 'string') {
        return false;
    }
    return value.length > 0;
}

/**
 * Handle both the provided onClick handler and
 * the one for the ripple effect.
 */
function useRippleEffectClick() {
    var ref = useRef(null);
    useEffect(function () {
        var current = ref.current;
        if (!current) {
            return;
        }
        current.addEventListener('click', handleRippleClick);
        return function () {
            current.removeEventListener('click', handleRippleClick);
        };
    }, [ref]);
    return ref;
}
/**
 * Add the element used in the ripple animation as a child of the event currentTarget.
 * The animation depends on the css styles in `rippleButtonStyles`.
 */
function handleRippleClick(event) {
    var _a;
    var buttonElement = event.currentTarget;
    var pos = buttonElement.getBoundingClientRect();
    var circle = document.createElement('span');
    var diameter = Math.max(buttonElement.clientWidth, buttonElement.clientHeight);
    var radius = diameter / 2;
    circle.style.width = circle.style.height = "".concat(diameter, "px");
    circle.style.left = "".concat(event.clientX - pos.left - radius, "px");
    circle.style.top = "".concat(event.clientY - pos.top - radius, "px");
    circle.classList.add('ripple');
    var ripple = (_a = buttonElement.getElementsByClassName('ripple')) === null || _a === void 0 ? void 0 : _a[0];
    if (ripple) {
        ripple.remove();
    }
    buttonElement.appendChild(circle);
}
var ripple = keyframes$1(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  to {\n    transform: scale(4);\n    opacity: 0;\n  }\n"], ["\n  to {\n    transform: scale(4);\n    opacity: 0;\n  }\n"])));
var rippleButtonStyles = function (_a) {
    var _b;
    var rippleColor = _a.rippleColor;
    return (_b = {},
        _b['span.ripple'] = {
            position: 'absolute',
            borderRadius: '50%',
            transform: 'scale(0)',
            animationName: "".concat(ripple),
            animationDuration: '600ms',
            animationTimingFunction: 'linear',
            backgroundColor: rippleColor ? rippleColor : 'ripples.backgroundColor',
            pointerEvents: 'none'
        },
        _b);
};
var templateObject_1$1;

var RippleButtonStyles = chakra(Button, {
    baseStyle: function (props) { return rippleButtonStyles(
    // any used as it's not clear how to type StyleResolverProps
    props); }
});

/**
 * A 3p branded theme Button with the material ui ripple effect. The effect is not possible in css only, hence
 * this component extends the core Chakra button for the effect.
 *
 * @see 3p Figma design https://www.figma.com/file/AVVZO09XE8fZ0uuOaJYcQg/B2C-Onboarding---for-Dev?node-id=2759%3A24812
 * @see ripple effect mui https://mui.com/components/buttons/#main-content
 */
var RippleButton = function (props) {
    var ref = useRippleEffectClick();
    return (React.createElement(RippleButtonStyles, __assign({}, props, { ref: ref })));
};

var RippleIconButtonStyles = chakra(IconButton, {
    baseStyle: function (props) { return rippleButtonStyles(
    // any used as it's not clear how to type StyleResolverProps
    props); }
});

/**
 * A 3p branded theme Button with the material ui ripple effect. The effect is not possible in css only, hence
 * this component extends the core Chakra button for the effect.
 *
 * @see 3p Figma design https://www.figma.com/file/AVVZO09XE8fZ0uuOaJYcQg/B2C-Onboarding---for-Dev?node-id=2759%3A24812
 * @see ripple effect mui https://mui.com/components/buttons/#main-content
 */
var RippleIconButton = function (props) {
    var ref = useRippleEffectClick();
    return (React.createElement(RippleIconButtonStyles, __assign({}, props, { ref: ref })));
};

var DEFAULT_TOTAL_DOTS = 7;
var dotKeyframes = keyframes$1(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  0% {\n    opacity: 0;\n    transform: scale(0.3);\n    background: #59CD90;\n  }\n  25% {\n    opacity: 1;\n    transform: scale(1.8);\n    background: #0072BB;\n  }\n  50% {\n    opacity: 0;\n    transform: scale(0.3);\n    background: #FE4A49;\n  }\n  75% {\n    opacity: 1;\n    transform: scale(1.8);\n    background: #FED766;\n  }\n  100% {\n    opacity: 0;\n  }\n"], ["\n  0% {\n    opacity: 0;\n    transform: scale(0.3);\n    background: #59CD90;\n  }\n  25% {\n    opacity: 1;\n    transform: scale(1.8);\n    background: #0072BB;\n  }\n  50% {\n    opacity: 0;\n    transform: scale(0.3);\n    background: #FE4A49;\n  }\n  75% {\n    opacity: 1;\n    transform: scale(1.8);\n    background: #FED766;\n  }\n  100% {\n    opacity: 0;\n  }\n"])));
var createDotAnimations = function (_a) {
    var _b = _a.totalDots, totalDots = _b === void 0 ? DEFAULT_TOTAL_DOTS : _b, duration = _a.duration;
    return Object.fromEntries(Array.from(Array(totalDots)).map(function (_, index) {
        var delay = totalDots / ((totalDots + 1) - index);
        return [
            "div:nth-of-type(".concat(index + 1, ")"),
            {
                animationDuration: "".concat(duration, "s"),
                animationDelay: "".concat(delay, "s"),
                animationName: "".concat(dotKeyframes),
                animationTimingFunction: 'linear',
                animationIterationCount: 'infinite'
            }
        ];
    }));
};
function LoaderDotAnimation(props) {
    var dotSpacing = props.dotSpacing, dotSize = props.dotSize; props.totalDots; props.duration; var restProps = __rest(props, ["dotSpacing", "dotSize", "totalDots", "duration"]);
    return (React.createElement(chakra.div, __assign({ position: 'relative', display: 'flex', sx: __assign({ div: __assign(__assign({ borderRadius: '50%' }, dotSize && {
                minHeight: "".concat(dotSize, "px"),
                minWidth: "".concat(dotSize, "px")
            }), dotSpacing && {
                'marginRight': "".concat(dotSpacing, "px"),
                '&:last-child': { marginRight: '0 !important' }
            }) }, createDotAnimations(props)) }, restProps)));
}
var templateObject_1;

/**
 * Visual loading indicator which displays a horizontal row of dots and
 * animates their color and size.
 */
function LoaderDots(_a) {
    var _b = _a.totalDots, totalDots = _b === void 0 ? DEFAULT_TOTAL_DOTS : _b, _c = _a.duration, duration = _c === void 0 ? 4 : _c, _d = _a.dotSize, dotSize = _d === void 0 ? 10 : _d, _e = _a.dotSpacing, dotSpacing = _e === void 0 ? 15 : _e, _f = _a["data-testid"], dataTestId = _f === void 0 ? 'LoaderDots' : _f;
    return (React.createElement(LoaderDotAnimation, { "data-testid": dataTestId, totalDots: totalDots, dotSize: dotSize, dotSpacing: dotSpacing, duration: duration }, Array.from(Array(totalDots)).map(function (_, index) {
        return (React.createElement("div", { key: index }));
    })));
}

function ToastView(_a) {
    var children = _a.children, onClose = _a.onClose, Icon = _a.Icon, _b = _a.borderColor, borderColor = _b === void 0 ? 'neutral.200' : _b;
    return (React.createElement(HStack, { "data-testid": 'toast-view', spacing: '8px', p: {
            base: '14px',
            medium: '24px'
        }, border: '1px solid', borderColor: borderColor, borderRadius: '12px', boxShadow: '0px 2px 20px rgba(0, 25, 55, 0.08)', color: 'neutral.700', letterSpacing: '-0.1px', fontWeight: '400', fontSize: {
            mobile: '14px',
            tablet: '16px'
        }, minW: {
            base: '100%',
            large: '548px'
        }, maxW: '548px', w: '100%', alignItems: 'flex-start', justifyContent: 'space-between', background: 'white' },
        React.createElement(HStack, { alignItems: 'flex-start' },
            Icon && React.createElement(Icon, null),
            React.createElement(Box, null, children)),
        React.createElement(CloseButton, { onClick: onClose })));
}
function CloseButton(props) {
    return (React.createElement(RippleButton, __assign({ variant: 'unstyled', minWidth: '24px', width: "24px", height: "24px" }, props),
        React.createElement(chakra.svg, { minWidth: '24px', width: "24px", height: "24px", viewBox: "0 0 25 25", fill: "none" },
            React.createElement("path", { d: "M5.52678 5.82005C5.93556 5.40206 6.60794 5.39268 7.02859 5.79908L11.9285 10.5331L16.6778 5.67684C17.1207 5.22403 17.8491 5.21386 18.3048 5.65413V5.65413C18.7605 6.0944 18.7709 6.81839 18.3281 7.27121L13.5788 12.1275L18.4534 16.837C18.874 17.2434 18.8836 17.9117 18.4749 18.3297V18.3297C18.0661 18.7477 17.3937 18.7571 16.9731 18.3507L12.0985 13.6411L7.3492 18.4974C6.90636 18.9502 6.17795 18.9604 5.72225 18.5201V18.5201C5.26655 18.0799 5.25612 17.3559 5.69895 16.9031L10.4482 12.0468L5.54829 7.31273C5.12764 6.90633 5.11801 6.23803 5.52678 5.82005V5.82005Z", fill: "#001937" }))));
}

function useMathleticsToast(options) {
    var toast = useToast(__assign(__assign({ position: 'top', containerStyle: {
            width: '100%',
            paddingLeft: '24px',
            paddingRight: '24px',
            marginTop: '16px'
        } }, options), { isClosable: true }));
    return function (innerOptions) { return toast(__assign(__assign({}, innerOptions), { render: function (_a) {
            var onClose = _a.onClose;
            var Icon = null;
            var borderColor = undefined;
            if ((innerOptions === null || innerOptions === void 0 ? void 0 : innerOptions.status) === 'error') {
                Icon = ErrorIcon;
                borderColor = 'error.700';
            }
            return (React.createElement(ToastView, { borderColor: borderColor, onClose: onClose, Icon: Icon },
                React.createElement("span", { dangerouslySetInnerHTML: {
                        // @ts-ignore allow this
                        __html: innerOptions.description
                    } })));
        } })); };
}
function ErrorIcon() {
    return (React.createElement(chakra.svg, { minWidth: '24px', width: "24px", height: "24px", viewBox: "0 0 24 24", fill: "none" },
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M21.6004 11.9999C21.6004 17.3018 17.3023 21.5999 12.0004 21.5999C6.69846 21.5999 2.40039 17.3018 2.40039 11.9999C2.40039 6.69797 6.69846 2.3999 12.0004 2.3999C17.3023 2.3999 21.6004 6.69797 21.6004 11.9999ZM13.2004 16.7999C13.2004 17.4626 12.6631 17.9999 12.0004 17.9999C11.3376 17.9999 10.8004 17.4626 10.8004 16.7999C10.8004 16.1372 11.3376 15.5999 12.0004 15.5999C12.6631 15.5999 13.2004 16.1372 13.2004 16.7999ZM12.0004 5.9999C11.3376 5.9999 10.8004 6.53716 10.8004 7.1999V11.9999C10.8004 12.6626 11.3376 13.1999 12.0004 13.1999C12.6631 13.1999 13.2004 12.6626 13.2004 11.9999V7.1999C13.2004 6.53716 12.6631 5.9999 12.0004 5.9999Z", fill: "#C51B29" })));
}

var ColorPalettes = function (_a) {
    var color = _a.color;
    var theme = useTheme();
    var keys = Object.keys(theme.colors[color]);
    return (React.createElement(ColorWrapper, null, keys.map(function (item) { return (React.createElement(ColorPalette, { key: "".concat(color, ".").concat(item), color: "".concat(color, ".").concat(item), name: "".concat(color, " ").concat(item) })); })));
};
function ColorPalette(_a) {
    var color = _a.color, name = _a.name, rest = __rest(_a, ["color", "name"]);
    var theme = useTheme();
    var colorCode = color;
    var _b = color.split('.'), shade = _b[0], hue = _b[1];
    if (shade && hue) {
        colorCode = theme.colors[shade][hue];
    }
    if (color in theme.colors && typeof theme.colors[color] === 'string') {
        colorCode = theme.colors[color];
    }
    return (React.createElement(Flex, __assign({ align: 'center' }, rest),
        React.createElement(Box, { borderRadius: 'md', boxSize: '3rem', boxShadow: 'inner', mr: 3, w: 10, h: 10, bg: color }),
        React.createElement(Box, { fontSize: 'sm' },
            React.createElement(Box, { fontWeight: 'semibold', textTransform: 'capitalize' }, name),
            React.createElement(Box, { textTransform: 'uppercase' }, colorCode),
            React.createElement(Box, null,
                "Token: ",
                React.createElement("pre", null, color)))));
}
function ColorWrapper(props) {
    return (React.createElement(Grid, __assign({ mt: 7, gap: 6, templateColumns: 'repeat( auto-fit, minmax(200px, 1fr) )' }, props)));
}

export { ColorPalette, ColorPalettes, ColorWrapper, LoaderDots, RippleButton, RippleIconButton, SelectControl, TextInputControl, assert, assignStringBoolean, convertHextoRGBColor, getColor, isDefined, theme, useMathleticsToast };
//# sourceMappingURL=index.js.map
